/**
 * Check if branch is active
 *
 * @returns {boolean} true if branch is active
 */
function isActive() {
  return !!(
    typeof window !== 'undefined' &&
    window.branch &&
    window.branch.logEvent
  );
}

/**
 * Add common context information for tracking
 *
 * @param {Object} attributes event attributes
 * @returns {Object} extended attributes
 */
function addContextData(attributes) {
  return Object.assign(
    {},
    {
      request_referrer: document.referrer,
      request_host: window.location.host,
      request_pathname: window.location.pathname,
      request_search: window.location.search,
    },
    attributes
  );
}

/**
 * Track navigation event corresponding `options`
 *
 * @param {Object} options values to track
 * @returns {Promise<Object>} execution result
 */
function trackNavigation(options) {
  const { name, attributes, items = [] } = options;
  if (!name) {
    return Promise.reject(
      new Error('Branch: Expected name to be defined for tracking navigation.')
    );
  }

  try {
    const attributesWithExtras = addContextData(attributes);
    window.branch.logEvent(name, { metadata: attributesWithExtras }, items);
    return Promise.resolve({ done: true });
  } catch (err) {
    return Promise.reject(
      new Error(`Branch: Could not execute branch.logEvent. Got error: ${err}.`)
    );
  }
}

/**
 * Track login by delegating to branch.setIdentity
 *
 * @param {Object} options user attributes for login
 * @returns {Promise<Object>} execution result
 */
function trackLogin(options) {
  const { attributes } = options;
  if (!attributes) {
    return Promise.reject(
      new Error('Branch: Expected attributes to be defined for login.')
    );
  }

  const { id } = attributes;
  if (!id) {
    return Promise.reject(
      new Error('Branch: Expected id to be defined for login.')
    );
  }

  return new Promise((resolve, reject) => {
    try {
      window.branch.setIdentity(id.toString(), err => {
        if (err) {
          reject(
            new Error(
              // eslint-disable-next-line max-len
              `Branch: Unsuccessful response while setting user identity: ${id}. Got error: ${err}`
            )
          );
          return;
        }

        resolve({ done: true });
      });
    } catch (err) {
      reject(
        new Error(
          `Branch: Could not execute branch.setIdentity. Got error: ${err}`
        )
      );
    }
  });
}

/**
 * Track logout by delegating to branch logout
 *
 * @returns {Promise<Object>} execution result
 */
function trackLogout() {
  return new Promise((resolve, reject) => {
    try {
      window.branch.logout();
      resolve({ done: true });
    } catch (err) {
      reject(
        new Error(`Branch: Could not execute branch.logout. Got error: ${err}`)
      );
    }
  });
}

module.exports = { isActive, trackNavigation, trackLogin, trackLogout };
